
import { isPwa } from "utils/pwa";
import fetchWrapper from "utils/fetchWrapper";
import { updateDocumentFavBoolean } from "utils/DexieDBManager/DexieDBManager";
import { appInsightsTracker } from "utils/AppInsightsManager/appInsights.utils";
import { APPLICABLE_STATE_NAME, STARRED_ACTION_TYPE } from "utils/DexieDBManager/dexieDB.models";

import {
  getAccessToken,
  AsyncResponseData,
  constructResponseData,
} from "utils/fetchWrapper/fetchWrapper";
import {
  db,
  IPdfFile,
  IPdfFileMeta,
  putPdfDocument,
  updateSettings,
  getSettingByKey,
  putPdfDocumentMeta,
  ISynchronizationItem,
  updatePdfDocumentAckDate,
  updatePdfDocumentReadDate,
  getAllSynchronizationItemsMeta,
  updateDocumentPdfFilesMetaFavorites,
  updateSynchronizationItemMetaIsAckRequiredFavoritePdfDocuments,
} from "utils/DexieDBManager";

enum ACTION_TYPE {
  ADD_OR_UPDATE_SYNCHRONIZATION_TO_INDEXED_DB = "ADD_OR_UPDATE_SYNCHRONIZATION_TO_INDEXED_DB",
  UPDATE_SYNCHRONIZATION_TO_INDEXED_DB = "UPDATE_SYNCHRONIZATION_TO_INDEXED_DB",
}

type MessageData = {
  synchronizationItems: ISynchronizationItem[];
  haveToSynchronizeWithDB?: boolean;
};

const getHeaderDataAndSendMessageToSW = async (
  actionType: ACTION_TYPE,
  messageData: MessageData
) => {
  const request = {
    Authorization: "Bearer " + (navigator.onLine ? await getAccessToken() : ""),
    url: process.env.REACT_APP_BACK_END_API,
  };

  navigator.serviceWorker?.controller?.postMessage({
    type: actionType,
    request: JSON.stringify(request),
    payload: JSON.stringify(messageData),
  });
};

type GetSynchronizationItemsAsyncOptions = {
  isOnlyAck?: boolean;
  isOnlyFav?: boolean;
  hasToSyncDocuments?: boolean;
};

type SynchronizationItems = {
  items: ISynchronizationItem[];
};

function sortByCategoryOrder(items: ISynchronizationItem[]) {
  return [...items].sort((a, b) => a.categoryOrder - b.categoryOrder);
}

export const getSynchronizationItemsAsync = async (userId: string, options: GetSynchronizationItemsAsyncOptions): Promise<AsyncResponseData<SynchronizationItems>> => {

  const isOnlyFavRequest = Boolean(options.isOnlyFav);
  const isOnlyAckRequest = Boolean(options.isOnlyAck);

const suffixeURI = isOnlyFavRequest 
  ? `versions?onlyFav=${options.isOnlyFav}`
  : isOnlyAckRequest
  ? `versions?onlyAck=${options.isOnlyAck}`
  : "versions";


  const response = await fetchWrapper.get(
    `${process.env.REACT_APP_BACK_END_API}/users/${userId}/${suffixeURI}`
  );
  const failureMessage = `Le chargement des synchronizationItems a échoué`;

  const synchronizationItemsData = await constructResponseData<{items: ISynchronizationItem[]}>(response, failureMessage);

  const HaveToUpdateData = isPwa && !isOnlyFavRequest && synchronizationItemsData.isSuccess;
  
  const sortedByCategoryOrder = sortByCategoryOrder(synchronizationItemsData?.responseData?.items ?? []);

  if (HaveToUpdateData) {
    const lastSynchroDate = await getSettingByKey("lastSynchroDate");

    if (!lastSynchroDate?.value) {
      updateSettings({key: "lastSynchroDate", value: new Date().toISOString()});
    }

    if (Boolean(options.hasToSyncDocuments) && synchronizationItemsData.responseData) {
      const haveToSynchronizeWithDBSetting = await getSettingByKey("versionsApiHasBeenCalled");


      getHeaderDataAndSendMessageToSW(ACTION_TYPE.ADD_OR_UPDATE_SYNCHRONIZATION_TO_INDEXED_DB,
        {
          synchronizationItems: sortedByCategoryOrder,
          haveToSynchronizeWithDB: !Boolean(haveToSynchronizeWithDBSetting?.value),
        }
      );
    }
  }

  return {...synchronizationItemsData, responseData: {...synchronizationItemsData.responseData, items: sortedByCategoryOrder}};
};

export const getVersionsAsync = async (userId: string): Promise<AsyncResponseData<SynchronizationItems>> => {

  const response = await fetchWrapper.get(`${process.env.REACT_APP_BACK_END_API}/users/${userId}/versions`);

  const failureMessage = `Le chargement des synchronizationItems a échoué`;

  const synchronizationItemsData = await constructResponseData<{items: ISynchronizationItem[]}>(response, failureMessage);
  
  return synchronizationItemsData;
};

export const getSyncDispatchItemsAsync = async (
  userId: string
): Promise<AsyncResponseData<SynchronizationItems>> => {
  const lastSynchroDate = (await getSettingByKey("lastSynchroDate"))?.value;

  const response = await fetchWrapper.get(
    `${process.env.REACT_APP_BACK_END_API}/users/${userId}/syncVersions?date=${lastSynchroDate}`
  );
  const failureMessage = `Le chargement des synchronizationItems a échoué`;

  const synchronizationItemsData = await constructResponseData<{
    items: ISynchronizationItem[];
  }>(response, failureMessage);
  if (isPwa && synchronizationItemsData.responseData) {
    getHeaderDataAndSendMessageToSW(
      ACTION_TYPE.UPDATE_SYNCHRONIZATION_TO_INDEXED_DB,
      {
        synchronizationItems: synchronizationItemsData.responseData.items,
      }
    ).then(() => {
      updateSettings({
        key: "lastSynchroDate",
        value: new Date().toISOString(),
      });
      getAllSynchronizationItemsMeta();
    });
  }

  return synchronizationItemsData;
};

type UpdatePdfDocumentReadDateAsyncInput = {
  documentVersionId: string;
  parentDocumentId: string;
  fileId: string;
};
export const updatePdfDocumentReadDateAsync = async ({
  fileId,
  parentDocumentId,
  documentVersionId,
}: UpdatePdfDocumentReadDateAsyncInput): Promise<AsyncResponseData<never>> => {
  const readDate = new Date().toISOString();
  const url = `${process.env.REACT_APP_BACK_END_API}/documents/${parentDocumentId}/versions/${documentVersionId}/read?readDate=${readDate}`;
  const response = await fetchWrapper.post(url);
  const failureMessage =
    "La mise à jour de la dernière date de lecture a échoué";

  const updatedPdfDocumentReadDate = await constructResponseData<never>(
    response,
    failureMessage
  );

  if (updatedPdfDocumentReadDate.isSuccess) {
    updatePdfDocumentReadDate(fileId, readDate);
    // TODO remove thde code that depends on updateSynchronizationItemMetaNotReadPdfDocuments
    // if (!isAlreadyRead) {
    //   updateSynchronizationItemMetaNotReadPdfDocuments(
    //     synchronizationId,
    //     documentVersionType
    //   );
    // }
  }
  return updatedPdfDocumentReadDate;
};

type UpdatePdfDocumentAckDateInput = {
  fileId: string;
  parentDocumentId: string;
  documentVersionId: string;
};

export const updatePdfDocumentAckDateAsync = async ({
  fileId,
  parentDocumentId,
  documentVersionId,
}: UpdatePdfDocumentAckDateInput): Promise<AsyncResponseData<never>> => {
  const ackDate = new Date().toISOString();
  const url = `${process.env.REACT_APP_BACK_END_API}/documents/${parentDocumentId}/versions/${documentVersionId}/ack?ackDate=${ackDate}`;
  const response = await fetchWrapper.post(url);
  const failureMessage = "La mise à jour de la date d'ack a échoué";

  const updatedPdfDocumentAckDate = await constructResponseData<never>(
    response,
    failureMessage
  );

  if (updatedPdfDocumentAckDate.isSuccess) {
    updatePdfDocumentAckDate(fileId, ackDate);
  }

  return updatedPdfDocumentAckDate;
};

type PdfDocumentToAddOrRemoveFromFavorites = {
  documentVersionType: APPLICABLE_STATE_NAME;
  synchronizationId: string;
  parentDocumentId: string;
  isAckRequired: boolean;
  fileId: string;
};

export const addPdfDocumentToFavoritesAsync = async ({
  fileId,
  isAckRequired,
  parentDocumentId,
  synchronizationId,
  documentVersionType,
}: PdfDocumentToAddOrRemoveFromFavorites): Promise<
  AsyncResponseData<never>
> => {
  const response = await fetchWrapper.post(
    `${process.env.REACT_APP_BACK_END_API}/documents/${parentDocumentId}/fav`
  );
  const failureMessage = "La mise à jour du favori a échoué";

  const addedPdfDocumentToFav = await constructResponseData<never>(
    response,
    failureMessage
  );
  const isFav = true;

  if (addedPdfDocumentToFav.isSuccess && isPwa) {
    updateDocumentPdfFilesMetaFavorites(fileId, isFav);
    updateSynchronizationItemMetaIsAckRequiredFavoritePdfDocuments({
      isAckRequired,
      synchronizationId,
      documentVersionType,
      starredActionType: STARRED_ACTION_TYPE.ADD, 
    });
    updateDocumentFavBoolean(
      synchronizationId,
      documentVersionType,
      fileId,
      isFav
    );
  }

  return addedPdfDocumentToFav;
};

export const deletePdfDocumentFromFavoritesAsync = async ({
  fileId,
  isAckRequired,
  parentDocumentId,
  synchronizationId,
  documentVersionType,
}: PdfDocumentToAddOrRemoveFromFavorites): Promise<
  AsyncResponseData<never>
> => {
  const response = await fetchWrapper.delete(
    `${process.env.REACT_APP_BACK_END_API}/documents/${parentDocumentId}/fav`
  );
  const failureMessage = "La suppression du favori a échoué";
  const isFav = false;

  const deletedPdfDocumentFromFav = await constructResponseData<never>(
    response,
    failureMessage
  );
if (deletedPdfDocumentFromFav.isSuccess && isPwa) {
    updateDocumentPdfFilesMetaFavorites(fileId, isFav);
    updateSynchronizationItemMetaIsAckRequiredFavoritePdfDocuments({
      isAckRequired,
      synchronizationId,
      documentVersionType,
      starredActionType: STARRED_ACTION_TYPE.DELETE
    });
    updateDocumentFavBoolean(
      synchronizationId,
      documentVersionType,
      fileId,
      isFav
    );

    try {
      const synchronizationItem = await db.synchronizationItems.get(
        synchronizationId
      );
      const versionDoc =
        documentVersionType === "APPLICABLE"
          ? synchronizationItem!.applicables
          : synchronizationItem!.nextApplicables;

      if (synchronizationItem && versionDoc) {
        const applicableItemToUpdate = versionDoc.find(
          (item) => item.file.id === fileId
        );

        if (applicableItemToUpdate) {
          applicableItemToUpdate.uds.isFav = false;

          await db.synchronizationItems.put(synchronizationItem);

          console.log("La valeur de isFav a été mise à jour avec succès.");
        } else {
          console.error(
            "L'élément avec l'ID spécifié n'a pas été trouvé dans le tableau 'applicable'."
          );
        }
      } else {
        console.error(
          "La synchronisation ou le tableau 'applicable' est introuvable."
        );
      }
    } catch (error) {
      console.error(
        "Une erreur s'est produite lors de la mise à jour de la valeur de isFav :",
        error
      );
    }
  }

  return deletedPdfDocumentFromFav;
};

export const downloadPdfFileBinaryByIdAsync = async (
  pdfFile: IPdfFile,
  isOffline: boolean
): Promise<AsyncResponseData<ArrayBuffer>> => {
  const response = await fetchWrapper.get(
    `${process.env.REACT_APP_BACK_END_API}/files/${pdfFile.id}/binary`
  );
  const failureMessage = "le chargement du fichier pdf a échoué";

  const fileBinaryData = await constructResponseData<never>(
    response,
    failureMessage,
    {
      dataType: "arrayBuffer",
    }
  );

  if (fileBinaryData.isSuccess && fileBinaryData.responseData) {
    const pdfBuffer = fileBinaryData.responseData;
    const pdfDocument = { ...pdfFile, pdfBuffer };

    if (isOffline && isPwa) {
      putPdfDocument(pdfDocument).then((updated) => {
        if (updated) {
          const pdfDocumentMeta: IPdfFileMeta = {
            ...pdfFile,
            isBufferSavedInDB: true,
          };
          putPdfDocumentMeta(pdfDocumentMeta);
          const traceMessage = `le binaire du pdf ${pdfFile.id} est mis à jour sur la base de données (IndexedDB)`;
          console.info(traceMessage, updated);
          appInsightsTracker.trackTrace(traceMessage);
        } else {
          const warnMessage = `le pdf id: ${pdfFile.id} n'est synchronisé sur la base de données (IndexedDB)`;
          console.warn(warnMessage);
          appInsightsTracker.trackException(warnMessage);
        }
      });
    }
  }

  return fileBinaryData;
};
